/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';

import { WorkspaceSubMenu as BaseWorkspaceSubMenu } from '@coze-foundation/space-ui-base';
import { useSpaceApp, useSpaceStore } from '@coze-foundation/space-store';
import { I18n } from '@coze-arch/i18n';
import {
  IconCozArrowDown,
  IconCozBot,
  IconCozBotFill,
  IconCozCheckMark,
  IconCozKnowledge,
  IconCozKnowledgeFill,
  IconCozSetting,
  IconCozTeamFill,
} from '@coze-arch/coze-design/icons';
import { Space, Avatar, Typography, Dropdown } from '@coze-arch/coze-design';
import { useRouteConfig } from '@coze-arch/bot-hooks';
import { EVENT_NAMES, sendTeaEvent } from '@coze-arch/bot-tea';
import { SpaceType } from '@coze-arch/idl/developer_api';

import { SpaceSubModuleEnum } from '@/const';

interface ISpaceSwitchItemProps {
  spaceId?: string;
  name?: string;
  iconUrl?: string;
  active?: boolean;
  onSelect: (spaceId: string) => void;
  dataTestId?: string;
}

const SpaceSwitchItem: FC<ISpaceSwitchItemProps> = ({
  spaceId,
  name,
  iconUrl,
  active,
  onSelect,
  dataTestId,
}) => (
  <div
    data-testid={dataTestId}
    onClick={() => {
      if (spaceId) {
        onSelect(spaceId);
      }
    }}
    className={`flex items-center gap-[8px] h-[36px] px-[8px] rounded-[8px] cursor-pointer hover:coz-mg-secondary-hovered${
      active ? ' coz-mg-secondary' : ''
    }`}
  >
    <Avatar
      className="w-[20px] h-[20px] rounded-[4px] shrink-0"
      src={iconUrl}
    />
    <Typography.Text
      className="flex-1 coz-fg-primary text-[14px]"
      ellipsis={{ showTooltip: true, rows: 1 }}
    >
      {name || ''}
    </Typography.Text>
    {active ? <IconCozCheckMark className="shrink-0" /> : null}
  </div>
);

const SECTION_LABEL_CLASS =
  'px-[8px] mb-[4px] text-[12px] leading-[16px] font-[500] coz-fg-primary';

export const WorkspaceSubMenu = () => {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const navigate = useNavigate();
  const { subMenuKey } = useRouteConfig();
  const spaceApp = useSpaceApp();

  const currentSpace = useSpaceStore(state => state.space);
  const spaceList = useSpaceStore(state => state.spaceList);

  const personalSpaces = spaceList.filter(
    space => space.space_type === SpaceType.Personal,
  );
  const teamSpaces = spaceList.filter(
    space => space.space_type === SpaceType.Team,
  );

  // Keep the current sub module (develop/library/organization/oauth) when switching spaces.
  // Fall back to develop on resource-specific pages (e.g. bot detail), whose
  // resource id belongs to the old space.
  const targetSubMenu = Object.values(SpaceSubModuleEnum).includes(
    spaceApp as SpaceSubModuleEnum,
  )
    ? (spaceApp as SpaceSubModuleEnum)
    : SpaceSubModuleEnum.DEVELOP;

  const handleSelectSpace = (spaceId: string) => {
    sendTeaEvent(EVENT_NAMES.space_switch_click, {
      current_space_id: currentSpace.id ?? '',
      to_space_id: spaceId,
    });
    setSwitcherOpen(false);
    if (spaceId !== currentSpace.id) {
      navigate(`/space/${spaceId}/${targetSubMenu}`);
    }
  };

  const subMenu = [
    {
      icon: <IconCozBot />,
      activeIcon: <IconCozBotFill />,
      title: () => I18n.t('navigation_workspace_develop', {}, 'Develop'),
      path: SpaceSubModuleEnum.DEVELOP,
      dataTestId: 'navigation_workspace_develop',
    },
    {
      icon: <IconCozKnowledge />,
      activeIcon: <IconCozKnowledgeFill />,
      title: () => I18n.t('navigation_workspace_library', {}, 'Library'),
      path: SpaceSubModuleEnum.LIBRARY,
      dataTestId: 'navigation_workspace_library',
    },
    {
      icon: <IconCozTeamFill />,
      activeIcon: <IconCozTeamFill />,
      title: () => I18n.t('navigation_workspace_organization', {}, '组织空间'),
      path: SpaceSubModuleEnum.ORGANIZATION,
      dataTestId: 'navigation_workspace_organization',
    },
    {
      icon: <IconCozSetting />,
      activeIcon: <IconCozSetting />,
      title: () => I18n.t('navigation_workspace_oauth', {}, 'OAuth 应用'),
      path: SpaceSubModuleEnum.OAUTH,
      dataTestId: 'navigation_workspace_oauth',
    },
  ];

  const headerNode = (
    <Dropdown
      trigger="custom"
      position="bottomLeft"
      visible={switcherOpen}
      onVisibleChange={setSwitcherOpen}
      render={
        <div
          data-testid="workspace.space-switch.panel"
          className="w-[240px] py-[8px] max-h-[420px] overflow-y-auto flex flex-col"
        >
          <div className={SECTION_LABEL_CLASS}>
            {I18n.t('menu_title_personal_space', {}, '个人空间')}
          </div>
          {personalSpaces.map(space => (
            <SpaceSwitchItem
              key={space.id}
              spaceId={space.id}
              name={space.name}
              iconUrl={space.icon_url}
              active={space.id === currentSpace.id}
              onSelect={handleSelectSpace}
              dataTestId={`workspace.space-switch.item.${space.id}`}
            />
          ))}
          {teamSpaces.length > 0 ? (
            <>
              <div className={`${SECTION_LABEL_CLASS} mt-[8px]`}>
                {I18n.t('menu_title_team_space', {}, '团队空间')}
              </div>
              {teamSpaces.map(space => (
                <SpaceSwitchItem
                  key={space.id}
                  spaceId={space.id}
                  name={space.name}
                  iconUrl={space.icon_url}
                  active={space.id === currentSpace.id}
                  onSelect={handleSelectSpace}
                  dataTestId={`workspace.space-switch.item.${space.id}`}
                />
              ))}
            </>
          ) : null}
        </div>
      }
    >
      <div
        data-testid="workspace.space-switch.button"
        className="cursor-pointer w-full"
        onClick={() => {
          const next = !switcherOpen;
          setSwitcherOpen(next);
          if (next) {
            sendTeaEvent(EVENT_NAMES.space_switch_show, {
              current_space_id: currentSpace.id ?? '',
            });
          }
        }}
      >
        <Space
          className="h-[48px] px-[8px] w-full hover:coz-mg-secondary-hovered rounded-[8px]"
          spacing={8}
        >
          <Avatar
            className="w-[24px] h-[24px] rounded-[6px] shrink-0"
            src={currentSpace?.icon_url}
          />
          <Typography.Text
            ellipsis={{ showTooltip: true, rows: 1 }}
            className="flex-1 coz-fg-primary text-[14px] font-[500]"
          >
            {currentSpace?.name || ''}
          </Typography.Text>
          <IconCozArrowDown className="shrink-0 coz-fg-secondary" />
        </Space>
      </div>
    </Dropdown>
  );

  return (
    <BaseWorkspaceSubMenu
      header={headerNode}
      menus={subMenu}
      currentSubMenu={subMenuKey}
    />
  );
};
